import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import * as Tone from 'tone';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  ArrowLeft, 
  Volume2, 
  VolumeX,
  Maximize,
  Minimize,
  Music2,
  Mic2,
  Settings,
  X,
  ChevronUp,
  ChevronDown,
  SlidersHorizontal,
  PartyPopper,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

export default function KaraokePlayer() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const audioRef = useRef(null);
  const vocalAudioRef = useRef(null);
  const lyricsContainerRef = useRef(null);
  const currentLineRef = useRef(null);
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(80);
  const [vocalVolume, setVocalVolume] = useState(0);
  const [muted, setMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [audioMode, setAudioMode] = useState('instrumental');
  const [showControls, setShowControls] = useState(true);
  const [currentLineIndex, setCurrentLineIndex] = useState(-1);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [lyrics, setLyrics] = useState([]);
  const [pitchShift, setPitchShift] = useState(0);
  const [showKeyControl, setShowKeyControl] = useState(false);
  const [showTitleScreen, setShowTitleScreen] = useState(true);
  const [titleScreenPhase, setTitleScreenPhase] = useState('intro'); // 'intro', 'outro', 'hidden'
  const [lyricsOffset, setLyricsOffset] = useState(0); // Offset in milliseconds
  const [showSyncControl, setShowSyncControl] = useState(false);
  const [mixerExpanded, setMixerExpanded] = useState(false);
  const [instrumentalVolume, setInstrumentalVolume] = useState(100);
  const [partyMode, setPartyMode] = useState(false);
  const [partyData, setPartyData] = useState(null);
  const [currentPartyIndex, setCurrentPartyIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(() => sessionStorage.getItem('partyAutoPlay') === 'true');
  const controlsTimeoutRef = useRef(null);
  const offsetSaveTimeoutRef = useRef(null);
  
  const tonePlayerRef = useRef(null);
  const vocalTonePlayerRef = useRef(null);
  const pitchShifterRef = useRef(null);
  const vocalPitchShifterRef = useRef(null);
  const toneInitializedRef = useRef(false);

  useEffect(() => {
    setLoading(true);
    setSession(null);
    setLyrics([]);
    setCurrentTime(0);
    setDuration(0);
    setPlaying(false);
    setCurrentLineIndex(-1);
    setCurrentWordIndex(-1);
    setShowTitleScreen(true);
    setTitleScreenPhase('intro');
    
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (vocalAudioRef.current) {
      vocalAudioRef.current.pause();
      vocalAudioRef.current.currentTime = 0;
    }
    
    fetchSession();
    
    if (searchParams.get('party') === 'true') {
      const storedParty = sessionStorage.getItem('partyPlaylist');
      if (storedParty) {
        try {
          const data = JSON.parse(storedParty);
          setPartyData(data);
          setPartyMode(true);
          const idx = data.songs.findIndex(s => s.id === sessionId);
          setCurrentPartyIndex(idx >= 0 ? idx : 0);
        } catch (e) {
          console.error('Failed to parse party data:', e);
        }
      }
    }
    
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      if (tonePlayerRef.current) {
        tonePlayerRef.current.dispose();
        tonePlayerRef.current = null;
      }
      if (vocalTonePlayerRef.current) {
        vocalTonePlayerRef.current.dispose();
        vocalTonePlayerRef.current = null;
      }
      if (pitchShifterRef.current) {
        pitchShifterRef.current.dispose();
        pitchShifterRef.current = null;
      }
      if (vocalPitchShifterRef.current) {
        vocalPitchShifterRef.current.dispose();
        vocalPitchShifterRef.current = null;
      }
      toneInitializedRef.current = false;
    };
  }, [sessionId]);

  useEffect(() => {
    if (pitchShifterRef.current) {
      pitchShifterRef.current.pitch = pitchShift;
    }
    if (vocalPitchShifterRef.current) {
      vocalPitchShifterRef.current.pitch = pitchShift;
    }
  }, [pitchShift]);

  // Auto-start playback when coming from auto-play party transition
  useEffect(() => {
    if (partyMode && session && sessionStorage.getItem('partyAutoStart') === 'true') {
      sessionStorage.removeItem('partyAutoStart');
      // Small delay to ensure audio is loaded
      const timer = setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play().catch(() => {});
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [partyMode, session]);

  useEffect(() => {
    const lyricsText = session?.custom_lyrics || session?.enhanced?.lyricsText;
    
    if (session?.enhanced?.words && lyricsText) {
      const lines = groupWordsIntoLinesFromEnhanced(
        session.enhanced.words,
        lyricsText
      );
      setLyrics(lines);
    } else if (session?.transcription?.words) {
      const lines = groupWordsIntoLines(session.transcription.words);
      setLyrics(lines);
    } else if (session?.custom_lyrics || session?.source_lyrics) {
      const text = session?.custom_lyrics || session?.source_lyrics;
      const lines = text.split('\n').filter(l => l.trim()).map((text, i) => ({
        text,
        words: [],
        startTime: i * 3,
        endTime: (i + 1) * 3
      }));
      setLyrics(lines);
    }
  }, [session]);

  useEffect(() => {
    if (currentLineRef.current && lyricsContainerRef.current) {
      currentLineRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [currentLineIndex]);

  const groupWordsIntoLinesFromEnhanced = (enhancedWords, lyricsText) => {
    if (!enhancedWords || enhancedWords.length === 0 || !lyricsText) return [];
    
    // Check if words have lineIndex property (new format)
    const hasLineIndex = enhancedWords.some(w => w.lineIndex !== undefined);
    
    if (hasLineIndex) {
      // Group words by their lineIndex property - this preserves original line structure
      const lineGroups = {};
      
      enhancedWords.forEach((word, globalIndex) => {
        const lineIdx = word.lineIndex ?? 0;
        if (!lineGroups[lineIdx]) {
          lineGroups[lineIdx] = [];
        }
        lineGroups[lineIdx].push({ ...word, globalIndex });
      });
      
      // Get the lyricsText lines for display text
      const textLines = lyricsText.split('\n').filter(l => l.trim());
      
      // Build lines array sorted by line index
      const lineIndices = Object.keys(lineGroups).map(Number).sort((a, b) => a - b);
      const lines = [];
      
      lineIndices.forEach((lineIdx) => {
        const lineWords = lineGroups[lineIdx];
        // Sort words within line by start time
        lineWords.sort((a, b) => a.start - b.start);
        
        // Use lyricsText line if available, otherwise build from words
        const displayText = textLines[lineIdx] || lineWords.map(w => w.word).join(' ');
        
        lines.push({
          text: displayText,
          words: lineWords,
          startTime: lineWords[0].start,
          endTime: lineWords[lineWords.length - 1].end
        });
      });
      
      return lines;
    }
    
    // Fallback for old format without lineIndex - use word count matching
    const textLines = lyricsText.split('\n').filter(l => l.trim());
    const lines = [];
    let wordIndex = 0;
    
    textLines.forEach((lineText) => {
      const lineWordCount = lineText.trim().split(/\s+/).length;
      const lineWords = [];
      
      for (let i = 0; i < lineWordCount && wordIndex < enhancedWords.length; i++) {
        const word = enhancedWords[wordIndex];
        lineWords.push({ ...word, globalIndex: wordIndex });
        wordIndex++;
      }
      
      if (lineWords.length > 0) {
        lines.push({
          text: lineText,
          words: lineWords,
          startTime: lineWords[0].start,
          endTime: lineWords[lineWords.length - 1].end
        });
      }
    });
    
    return lines;
  };

  const groupWordsIntoLines = (words) => {
    if (!words || words.length === 0) return [];
    
    const lines = [];
    let currentLine = { words: [], text: '', startTime: 0, endTime: 0 };
    let lastEndTime = 0;
    
    words.forEach((word, index) => {
      const gap = word.start - lastEndTime;
      const lineBreakGap = 1.5;
      
      if (gap > lineBreakGap && currentLine.words.length > 0) {
        currentLine.text = currentLine.words.map(w => w.word).join(' ');
        currentLine.endTime = currentLine.words[currentLine.words.length - 1].end;
        lines.push(currentLine);
        currentLine = { words: [], text: '', startTime: word.start, endTime: 0 };
      }
      
      if (currentLine.words.length === 0) {
        currentLine.startTime = word.start;
      }
      
      currentLine.words.push({ ...word, globalIndex: index });
      lastEndTime = word.end;
      
      if (currentLine.words.length >= 8) {
        currentLine.text = currentLine.words.map(w => w.word).join(' ');
        currentLine.endTime = currentLine.words[currentLine.words.length - 1].end;
        lines.push(currentLine);
        currentLine = { words: [], text: '', startTime: 0, endTime: 0 };
      }
    });
    
    if (currentLine.words.length > 0) {
      currentLine.text = currentLine.words.map(w => w.word).join(' ');
      currentLine.endTime = currentLine.words[currentLine.words.length - 1].end;
      lines.push(currentLine);
    }
    
    return lines;
  };

  const fetchSession = async () => {
    try {
      const response = await api.get(`/sessions/${sessionId}`);
      setSession(response.data);
      setLyricsOffset(response.data.lyrics_offset || 0);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load session');
      navigate('/dashboard');
    }
  };

  const saveLyricsOffset = async (offset) => {
    try {
      await api.put(`/sessions/${sessionId}`, { lyrics_offset: offset });
    } catch (error) {
      console.error('Failed to save lyrics offset:', error);
    }
  };

  const handleOffsetChange = (delta) => {
    const newOffset = lyricsOffset + delta;
    setLyricsOffset(newOffset);
    
    // Debounce save to avoid too many API calls
    if (offsetSaveTimeoutRef.current) {
      clearTimeout(offsetSaveTimeoutRef.current);
    }
    offsetSaveTimeoutRef.current = setTimeout(() => {
      saveLyricsOffset(newOffset);
    }, 1000);
  };

  const initializeToneAudio = async () => {
    if (toneInitializedRef.current) return;
    
    try {
      await Tone.start();
      
      if (audioRef.current && !tonePlayerRef.current) {
        const context = Tone.getContext();
        const mediaSource = context.createMediaElementSource(audioRef.current);
        
        pitchShifterRef.current = new Tone.PitchShift({
          pitch: pitchShift,
          windowSize: 0.1,
          delayTime: 0,
          feedback: 0
        }).toDestination();
        
        Tone.connect(mediaSource, pitchShifterRef.current);
      }
      
      if (vocalAudioRef.current && session?.files?.vocal_url && !vocalTonePlayerRef.current) {
        const context = Tone.getContext();
        const vocalMediaSource = context.createMediaElementSource(vocalAudioRef.current);
        
        vocalPitchShifterRef.current = new Tone.PitchShift({
          pitch: pitchShift,
          windowSize: 0.1,
          delayTime: 0,
          feedback: 0
        }).toDestination();
        
        Tone.connect(vocalMediaSource, vocalPitchShifterRef.current);
      }
      
      toneInitializedRef.current = true;
    } catch (error) {
      console.error('Failed to initialize Tone.js:', error);
    }
  };

  const handlePitchChange = (delta) => {
    const newPitch = Math.max(-5, Math.min(5, pitchShift + delta));
    setPitchShift(newPitch);
  };

  const handleTimeUpdate = useCallback(() => {
    if (!audioRef.current) return;
    const time = audioRef.current.currentTime;
    const audioDuration = audioRef.current.duration || duration;
    setCurrentTime(time);
    
    // Apply lyrics offset (convert ms to seconds) - positive offset = lyrics come earlier
    const offsetSeconds = lyricsOffset / 1000;
    const adjustedTime = time + offsetSeconds;
    
    if (vocalAudioRef.current && vocalVolume > 0 && !vocalAudioRef.current.paused) {
      const drift = Math.abs(vocalAudioRef.current.currentTime - time);
      if (drift > 0.1) {
        vocalAudioRef.current.currentTime = time;
      }
    }
    
    // Calculate first and last lyric times for intro/outro detection
    const firstLyricTime = lyrics.length > 0 ? lyrics[0].startTime : 0;
    const lastLine = lyrics[lyrics.length - 1];
    const lastLyricTime = lastLine ? (lastLine.endTime || lastLine.startTime + 5) : audioDuration;
    
    // Intro: before first lyrics (with 2 second buffer to fade out)
    const introEndTime = Math.max(0, firstLyricTime - 2);
    // Outro: after last lyrics end (with 2 second buffer)
    const outroStartTime = lastLyricTime + 2;
    
    if (adjustedTime < introEndTime || adjustedTime < 0.5) {
      if (titleScreenPhase !== 'intro') {
        setTitleScreenPhase('intro');
        setShowTitleScreen(true);
      }
    } else if (adjustedTime >= outroStartTime && adjustedTime < audioDuration - 0.5) {
      if (titleScreenPhase !== 'outro') {
        setTitleScreenPhase('outro');
        setShowTitleScreen(true);
      }
    } else {
      if (showTitleScreen) {
        setShowTitleScreen(false);
      }
    }
    
    const lineIndex = lyrics.findIndex((line, i) => {
      const nextLine = lyrics[i + 1];
      return adjustedTime >= line.startTime && (!nextLine || adjustedTime < nextLine.startTime);
    });
    setCurrentLineIndex(lineIndex);
    
    if (lineIndex >= 0 && lyrics[lineIndex]?.words?.length > 0) {
      const line = lyrics[lineIndex];
      const wordIdx = line.words.findIndex((word, i) => {
        const nextWord = line.words[i + 1];
        return adjustedTime >= word.start && (!nextWord || adjustedTime < nextWord.start);
      });
      setCurrentWordIndex(wordIdx);
    } else {
      setCurrentWordIndex(-1);
    }
  }, [lyrics, vocalVolume, duration, titleScreenPhase, showTitleScreen, lyricsOffset]);

  const handlePlayPause = async () => {
    if (audioRef.current) {
      if (playing) {
        audioRef.current.pause();
        if (vocalAudioRef.current) vocalAudioRef.current.pause();
      } else {
        await initializeToneAudio();
        audioRef.current.play();
        if (vocalAudioRef.current && vocalVolume > 0) {
          vocalAudioRef.current.currentTime = audioRef.current.currentTime;
          vocalAudioRef.current.play();
        }
      }
      setPlaying(!playing);
    }
  };

  const handleSeek = (value) => {
    const seekTime = (value[0] / 100) * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
    if (vocalAudioRef.current) {
      vocalAudioRef.current.currentTime = seekTime;
    }
  };

  const handleSkip = (seconds) => {
    if (audioRef.current) {
      const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
      if (vocalAudioRef.current) {
        vocalAudioRef.current.currentTime = newTime;
      }
    }
  };

  const handleVolumeChange = (value) => {
    const vol = value[0];
    setVolume(vol);
    if (audioRef.current) {
      audioRef.current.volume = vol / 100;
    }
    if (vol > 0 && muted) {
      setMuted(false);
    }
  };

  const handleVocalVolumeChange = (value) => {
    const vol = value[0];
    setVocalVolume(vol);
    if (vocalAudioRef.current) {
      vocalAudioRef.current.volume = (vol / 100) * (volume / 100);
      if (vol > 0 && playing && vocalAudioRef.current.paused) {
        vocalAudioRef.current.currentTime = audioRef.current?.currentTime || 0;
        vocalAudioRef.current.play();
      } else if (vol === 0 && !vocalAudioRef.current.paused) {
        vocalAudioRef.current.pause();
      }
    }
  };

  const handleInstrumentalVolumeChange = (value) => {
    const vol = value[0];
    setInstrumentalVolume(vol);
    if (audioRef.current) {
      audioRef.current.volume = (vol / 100) * (volume / 100);
    }
  };

  const handleMasterVolumeChange = (value) => {
    const vol = value[0];
    setVolume(vol);
    if (audioRef.current) {
      audioRef.current.volume = (instrumentalVolume / 100) * (vol / 100);
    }
    if (vocalAudioRef.current) {
      vocalAudioRef.current.volume = (vocalVolume / 100) * (vol / 100);
    }
    if (vol > 0 && muted) {
      setMuted(false);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !muted;
    }
    if (vocalAudioRef.current) {
      vocalAudioRef.current.muted = !muted;
    }
    setMuted(!muted);
  };

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const toggleAudioMode = () => {
    const newMode = audioMode === 'instrumental' ? 'original' : 'instrumental';
    setAudioMode(newMode);
    
    if (audioRef.current && session) {
      const currentPlayTime = audioRef.current.currentTime;
      const wasPlaying = !audioRef.current.paused;
      
      // Pause first to prevent "play() interrupted" error
      audioRef.current.pause();
      
      const audioUrl = newMode === 'instrumental' 
        ? session.files.instrumental_url 
        : session.files.original_url;
      
      audioRef.current.src = audioUrl;
      
      // Wait for audio to be ready before seeking and playing
      const handleCanPlay = () => {
        audioRef.current.currentTime = currentPlayTime;
        if (wasPlaying) {
          audioRef.current.play().catch(() => {});
        }
        audioRef.current.removeEventListener('canplay', handleCanPlay);
      };
      
      audioRef.current.addEventListener('canplay', handleCanPlay);
      audioRef.current.load();
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (playing) {
        setShowControls(false);
      }
    }, 5000);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const getAudioUrl = () => {
    if (!session?.files) return null;
    return audioMode === 'instrumental' 
      ? session.files.instrumental_url 
      : session.files.original_url;
  };

  const toggleAutoPlay = () => {
    const newValue = !autoPlay;
    setAutoPlay(newValue);
    sessionStorage.setItem('partyAutoPlay', newValue.toString());
    toast.success(newValue ? 'Auto-play enabled' : 'Auto-play disabled');
  };

  const handleSongEnd = () => {
    setPlaying(false);
    if (partyMode && partyData?.songs) {
      const nextIndex = currentPartyIndex + 1;
      if (nextIndex < partyData.songs.length) {
        const nextSong = partyData.songs[nextIndex];
        if (autoPlay) {
          // Auto-play: navigate immediately and start playing
          sessionStorage.setItem('partyAutoStart', 'true');
          navigate(`/sing/${nextSong.id}?party=true`);
        } else {
          toast.success(`Up next: ${nextSong.title}${nextSong.singer ? ` - ${nextSong.singer}` : ''}`);
          setTimeout(() => {
            navigate(`/sing/${nextSong.id}?party=true`);
          }, 2000);
        }
      } else {
        toast.success('Party complete! Great singing everyone!');
        setTimeout(() => {
          sessionStorage.removeItem('partyPlaylist');
          sessionStorage.removeItem('partyAutoPlay');
          sessionStorage.removeItem('partyAutoStart');
          navigate('/dashboard');
        }, 3000);
      }
    }
  };

  const goToNextSong = () => {
    if (partyMode && partyData?.songs && currentPartyIndex < partyData.songs.length - 1) {
      const nextSong = partyData.songs[currentPartyIndex + 1];
      navigate(`/sing/${nextSong.id}?party=true`);
    }
  };

  const goToPrevSong = () => {
    if (partyMode && partyData?.songs && currentPartyIndex > 0) {
      const prevSong = partyData.songs[currentPartyIndex - 1];
      navigate(`/sing/${prevSong.id}?party=true`);
    }
  };

  const exitPartyMode = () => {
    sessionStorage.removeItem('partyPlaylist');
    navigate('/dashboard');
  };

  const getCurrentSinger = () => {
    if (partyMode && partyData?.songs && partyData.songs[currentPartyIndex]) {
      return partyData.songs[currentPartyIndex].singer;
    }
    return null;
  };

  const InlineMixerChannel = ({ label, icon, value, onChange }) => {
    const presets = [0, 25, 50, 75, 100];
    
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 text-white/80 min-w-[60px]">
          {icon}
          <span className="text-xs font-medium">{label}</span>
        </div>
        
        <div className="flex gap-1.5 flex-1">
          {presets.map((preset) => (
            <button
              key={preset}
              onClick={() => onChange([preset])}
              className={`flex-1 h-9 rounded-lg text-sm font-medium transition-all ${
                value === preset
                  ? 'bg-primary text-white shadow-lg'
                  : 'bg-white/10 text-white/80 hover:bg-white/20 active:bg-white/30'
              }`}
            >
              {preset}%
            </button>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <img src="/logo.png" alt="Loading" className="h-16 w-16 mx-auto animate-pulse" />
          <p className="mt-4 text-white/60">Loading karaoke...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900 flex flex-col relative overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      <audio 
        ref={audioRef}
        src={getAudioUrl()}
        crossOrigin="anonymous"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
        onEnded={handleSongEnd}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      {session?.files?.vocal_url && (
        <audio 
          ref={vocalAudioRef}
          src={session.files.vocal_url}
          crossOrigin="anonymous"
        />
      )}

      <div className={`fixed top-0 left-0 right-0 z-50 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <div className="bg-gradient-to-b from-black/80 to-transparent p-3 md:p-4">
          <div className="flex items-center justify-between max-w-7xl mx-auto gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/dashboard')}
              className="text-white/80 hover:text-white hover:bg-white/10 h-10 w-10 md:h-auto md:w-auto md:px-3"
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="hidden md:inline ml-2">Back</span>
            </Button>
            
            <div className="text-center min-w-0 flex-1 px-2">
              <h1 className="text-base md:text-xl font-bold text-white truncate">
                {session?.title || session?.filename || 'Untitled'}
              </h1>
              {session?.artist && (
                <p className="text-white/60 text-xs md:text-sm truncate">{session.artist}</p>
              )}
            </div>
            
            <div className="flex items-center gap-1 md:gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setShowSyncControl(!showSyncControl); setShowKeyControl(false); }}
                className={`text-white/80 hover:text-white hover:bg-white/10 h-10 px-2 md:px-3 ${showSyncControl || lyricsOffset !== 0 ? 'bg-blue-500/20' : ''}`}
                title="Sync Lyrics Timing"
              >
                <span className="text-xs md:text-sm font-bold">
                  {lyricsOffset === 0 ? 'Sync' : `${lyricsOffset > 0 ? '+' : ''}${lyricsOffset}ms`}
                </span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setShowKeyControl(!showKeyControl); setShowSyncControl(false); }}
                className={`text-white/80 hover:text-white hover:bg-white/10 h-10 px-2 md:px-3 ${showKeyControl || pitchShift !== 0 ? 'bg-purple-500/20' : ''}`}
                title="Change Key"
              >
                <span className="text-xs md:text-sm font-bold">
                  {pitchShift === 0 ? 'Key' : (pitchShift > 0 ? `+${pitchShift}` : pitchShift)}
                </span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleFullscreen}
                className="text-white/80 hover:text-white hover:bg-white/10 h-10 w-10"
              >
                {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>
        
        {/* Party Mode Banner */}
        {partyMode && partyData && (
          <div className="bg-gradient-to-r from-pink-500/20 to-orange-500/20 border-b border-pink-500/30 px-3 py-2">
            <div className="flex items-center justify-between max-w-7xl mx-auto gap-2">
              <div className="flex items-center gap-3">
                <PartyPopper className="h-4 w-4 text-pink-400" />
                <span className="text-white/80 text-sm font-medium truncate">
                  {partyData.name}
                </span>
                <span className="text-white/50 text-xs">
                  Song {currentPartyIndex + 1} of {partyData.songs.length}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                {getCurrentSinger() && (
                  <span className="text-pink-300 text-sm font-medium hidden sm:block">
                    {getCurrentSinger()} is singing
                  </span>
                )}
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={goToPrevSong}
                    disabled={currentPartyIndex === 0}
                    className="text-white/60 hover:text-white h-8 w-8 p-0 disabled:opacity-30"
                    title="Previous song"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={goToNextSong}
                    disabled={currentPartyIndex === partyData.songs.length - 1}
                    className="text-white/60 hover:text-white h-8 w-8 p-0 disabled:opacity-30"
                    title="Next song"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleAutoPlay}
                    className={`h-8 px-2 text-xs ${autoPlay ? 'bg-green-500/30 text-green-300' : 'text-white/60 hover:text-white'}`}
                    title={autoPlay ? 'Auto-play on' : 'Auto-play off'}
                  >
                    Auto
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={exitPartyMode}
                    className="text-white/60 hover:text-white h-8 px-2 text-xs"
                    title="Exit party mode"
                  >
                    Exit Party
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Key Control Panel */}
      {showKeyControl && (
        <div className={`fixed top-16 md:top-20 right-2 md:right-4 z-50 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          <div className="bg-black/90 backdrop-blur-sm rounded-xl p-4 border border-white/10 shadow-xl">
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/80 text-sm font-medium">Transpose Key</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowKeyControl(false)}
                className="text-white/60 hover:text-white h-6 w-6 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex items-center justify-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePitchChange(-1)}
                disabled={pitchShift <= -5}
                className="h-10 w-10 rounded-full bg-white/5 border-white/20 hover:bg-white/10 text-white"
              >
                <ChevronDown className="h-5 w-5" />
              </Button>
              
              <div className="text-center min-w-[60px]">
                <div className={`text-2xl font-bold ${pitchShift === 0 ? 'text-white' : pitchShift > 0 ? 'text-green-400' : 'text-blue-400'}`}>
                  {pitchShift > 0 ? `+${pitchShift}` : pitchShift}
                </div>
                <div className="text-white/40 text-xs">semitones</div>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePitchChange(1)}
                disabled={pitchShift >= 5}
                className="h-10 w-10 rounded-full bg-white/5 border-white/20 hover:bg-white/10 text-white"
              >
                <ChevronUp className="h-5 w-5" />
              </Button>
            </div>
            
            {pitchShift !== 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPitchShift(0)}
                className="w-full mt-3 text-white/60 hover:text-white text-xs"
              >
                Reset to Original Key
              </Button>
            )}
            
            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="flex flex-wrap gap-1 justify-center">
                {[-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5].map((val) => (
                  <Button
                    key={val}
                    variant="ghost"
                    size="sm"
                    onClick={() => setPitchShift(val)}
                    className={`h-7 w-7 p-0 text-xs rounded-full ${
                      pitchShift === val 
                        ? 'bg-primary text-white' 
                        : 'text-white/40 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {val > 0 ? `+${val}` : val}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sync Control Panel */}
      {showSyncControl && (
        <div className={`fixed top-16 md:top-20 right-2 md:right-4 z-50 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          <div className="bg-black/90 backdrop-blur-sm rounded-xl p-4 border border-white/10 shadow-xl">
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/80 text-sm font-medium">Lyrics Timing</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSyncControl(false)}
                className="text-white/60 hover:text-white h-6 w-6 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <p className="text-white/50 text-xs mb-3 text-center">
              Adjust if lyrics appear too early or late
            </p>
            
            <div className="flex items-center justify-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOffsetChange(-50)}
                className="h-10 w-12 rounded-lg bg-white/5 border-white/20 hover:bg-white/10 text-white text-xs"
              >
                -50
              </Button>
              
              <div className="text-center min-w-[80px]">
                <div className={`text-2xl font-bold ${lyricsOffset === 0 ? 'text-white' : lyricsOffset > 0 ? 'text-green-400' : 'text-blue-400'}`}>
                  {lyricsOffset > 0 ? `+${lyricsOffset}` : lyricsOffset}
                </div>
                <div className="text-white/40 text-xs">milliseconds</div>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOffsetChange(50)}
                className="h-10 w-12 rounded-lg bg-white/5 border-white/20 hover:bg-white/10 text-white text-xs"
              >
                +50
              </Button>
            </div>
            
            <div className="flex items-center justify-center gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOffsetChange(-200)}
                className="h-8 px-2 rounded-lg bg-white/5 border-white/20 hover:bg-white/10 text-white text-xs"
              >
                -200ms
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOffsetChange(200)}
                className="h-8 px-2 rounded-lg bg-white/5 border-white/20 hover:bg-white/10 text-white text-xs"
              >
                +200ms
              </Button>
            </div>
            
            {lyricsOffset !== 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setLyricsOffset(0); saveLyricsOffset(0); }}
                className="w-full mt-3 text-white/60 hover:text-white text-xs"
              >
                Reset to Original Timing
              </Button>
            )}
            
            <div className="mt-3 pt-3 border-t border-white/10 text-center">
              <p className="text-white/40 text-xs">
                {lyricsOffset > 0 ? '+ = lyrics appear earlier' : lyricsOffset < 0 ? '- = lyrics appear later' : 'Adjust timing to match the audio'}
              </p>
            </div>
          </div>
        </div>
      )}

      
      {/* Title Screen - Intro/Outro */}
      <div 
        className={`fixed inset-0 z-40 flex items-center justify-center transition-opacity duration-1000 pointer-events-none ${
          showTitleScreen ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="text-center px-8">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 drop-shadow-lg">
            {session?.title || session?.filename || 'Untitled'}
          </h1>
          {session?.artist && (
            <p className="text-2xl md:text-3xl lg:text-4xl text-white/80 font-medium drop-shadow-md">
              {session.artist}
            </p>
          )}
          {titleScreenPhase === 'outro' && (
            <p className="mt-8 text-xl text-white/50 animate-pulse">
              Thanks for singing!
            </p>
          )}
        </div>
      </div>

      <div 
        ref={lyricsContainerRef}
        className={`flex-1 flex flex-col items-center justify-center px-4 md:px-8 py-24 md:py-32 overflow-y-auto transition-opacity duration-500 ${
          showTitleScreen ? 'opacity-0' : 'opacity-100'
        }`}
      >
        {lyrics.length > 0 ? (
          <div className="space-y-6 md:space-y-8 max-w-4xl w-full">
            {lyrics.map((line, lineIndex) => {
              const isCurrentLine = lineIndex === currentLineIndex;
              const isPastLine = lineIndex < currentLineIndex;
              const isUpcoming = lineIndex > currentLineIndex && lineIndex <= currentLineIndex + 2;
              
              return (
                <div 
                  key={lineIndex}
                  ref={isCurrentLine ? currentLineRef : null}
                  className={`text-center transition-all duration-500 ${
                    isCurrentLine 
                      ? 'scale-110 opacity-100' 
                      : isPastLine 
                        ? 'scale-90 opacity-30' 
                        : isUpcoming
                          ? 'scale-95 opacity-60'
                          : 'scale-90 opacity-20'
                  }`}
                >
                  {line.words?.length > 0 ? (
                    <p className="text-3xl md:text-5xl font-bold leading-relaxed">
                      {line.words.map((word, wordIndex) => {
                        const isCurrentWord = isCurrentLine && wordIndex === currentWordIndex;
                        const isPastWord = isCurrentLine && wordIndex < currentWordIndex;
                        
                        return (
                          <span 
                            key={wordIndex}
                            className={`inline-block mx-1 transition-all duration-200 ${
                              isCurrentWord 
                                ? 'text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-400 scale-110 animate-pulse' 
                                : isPastWord || isPastLine
                                  ? 'text-purple-300'
                                  : 'text-white'
                            }`}
                          >
                            {word.word}
                          </span>
                        );
                      })}
                    </p>
                  ) : (
                    <p className={`text-3xl md:text-5xl font-bold leading-relaxed ${
                      isCurrentLine 
                        ? 'text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-400' 
                        : isPastLine
                          ? 'text-purple-300'
                          : 'text-white'
                    }`}>
                      {line.text}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center px-4">
            <Music2 className="h-16 w-16 md:h-24 md:w-24 text-white/20 mx-auto mb-4" />
            <p className="text-white/40 text-lg md:text-xl">No lyrics available</p>
            <p className="text-white/20 text-xs md:text-sm mt-2">Enjoy the music!</p>
          </div>
        )}
      </div>

      <div className={`fixed bottom-0 left-0 right-0 z-50 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <div className="bg-gradient-to-t from-black/90 via-black/70 to-transparent pt-8 md:pt-16 pb-6 md:pb-8 px-3 md:px-4">
          <div className="max-w-4xl mx-auto space-y-3 md:space-y-4">
            
            {/* Collapsible Mixer Panel */}
            <div className={`overflow-hidden transition-all duration-300 ease-out ${mixerExpanded ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 space-y-3 mb-2" style={{ touchAction: 'none' }}>
                <InlineMixerChannel
                  label="Music"
                  icon={<Music2 className="h-4 w-4 text-cyan-400" />}
                  value={instrumentalVolume}
                  onChange={handleInstrumentalVolumeChange}
                />
                {session?.files?.vocal_url && (
                  <InlineMixerChannel
                    label="Vocals"
                    icon={<Mic2 className="h-4 w-4 text-pink-400" />}
                    value={vocalVolume}
                    onChange={handleVocalVolumeChange}
                  />
                )}
              </div>
            </div>
            
            {/* Progress bar */}
            <div className="flex items-center gap-2 md:gap-4">
              <span className="text-white/60 text-xs md:text-sm w-10 md:w-12 text-right">
                {formatTime(currentTime)}
              </span>
              <Slider
                value={[duration > 0 ? (currentTime / duration) * 100 : 0]}
                max={100}
                step={0.1}
                onValueChange={handleSeek}
                className="flex-1"
              />
              <span className="text-white/60 text-xs md:text-sm w-10 md:w-12">
                {formatTime(duration)}
              </span>
            </div>
            
            {/* Main controls - Mobile optimized */}
            <div className="flex items-center justify-between">
              {/* Mute button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleMute}
                className="text-white/80 hover:text-white hover:bg-white/10 h-12 w-12"
              >
                {muted || volume === 0 ? <VolumeX className="h-5 w-5 md:h-6 md:w-6" /> : <Volume2 className="h-5 w-5 md:h-6 md:w-6" />}
              </Button>
              
              {/* Playback controls */}
              <div className="flex items-center gap-2 md:gap-4">
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={() => handleSkip(-10)}
                  className="text-white/80 hover:text-white hover:bg-white/10 h-12 w-12 rounded-full"
                >
                  <SkipBack className="h-5 w-5 md:h-6 md:w-6" />
                </Button>
                
                <Button
                  size="lg"
                  onClick={handlePlayPause}
                  className="h-14 w-14 md:h-16 md:w-16 rounded-full bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90 shadow-lg shadow-primary/25"
                >
                  {playing ? <Pause className="h-7 w-7 md:h-8 md:w-8" /> : <Play className="h-7 w-7 md:h-8 md:w-8 ml-1" />}
                </Button>
                
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={() => handleSkip(10)}
                  className="text-white/80 hover:text-white hover:bg-white/10 h-12 w-12 rounded-full"
                >
                  <SkipForward className="h-5 w-5 md:h-6 md:w-6" />
                </Button>
              </div>
              
              {/* Mixer button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setMixerExpanded(!mixerExpanded); setShowKeyControl(false); setShowSyncControl(false); }}
                className={`text-white/80 hover:text-white hover:bg-white/10 h-12 w-12 ${mixerExpanded ? 'bg-purple-500/20' : ''}`}
                title="Audio Mixer"
              >
                <SlidersHorizontal className="h-5 w-5 md:h-6 md:w-6" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
